import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "@/components/Nav";
import { payments as paymentsApi } from "@/lib/api";
import { Check, X, Loader2, ArrowRight } from "lucide-react";

export default function PaymentResult({ status }) {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState({ status: "checking", projectId: null });
  const sessionId = params.get("session_id");
  const isSuccess = status === "success";

  useEffect(() => {
    if (!isSuccess || !sessionId) return;
    let tries = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const s = await paymentsApi.status(sessionId);
        if (s.payment_status === "paid") {
          setState({ status: "paid", projectId: s.project_id });
          return;
        }
        if (tries++ < 20) setTimeout(poll, 1500);
        else setState({ status: "unknown", projectId: s.project_id });
      } catch {
        if (!cancelled) setState({ status: "unknown", projectId: null });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, isSuccess]);

  // Auto-redirect back to the wizard for the correct project after 1.2s
  // so users are never stranded on Stripe's referrer in their history.
  useEffect(() => {
    if (state.status !== "paid" || !state.projectId) return;
    const t = setTimeout(() => {
      nav(`/create/${state.projectId}`, { replace: true });
    }, 1200);
    return () => clearTimeout(t);
  }, [state, nav]);

  const goToDeck = () => {
    if (state.projectId) nav(`/create/${state.projectId}`, { replace: true });
    else nav("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="pt-16 pb-16 px-6 flex items-center justify-center">
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="kd-panel p-10 md:p-14 max-w-lg w-full text-center">
          {isSuccess && state.status === "checking" && (
            <>
              <Loader2 size={40} className="mx-auto mb-6 animate-spin text-[#FF2A2D]" />
              <h1 className="kd-display text-3xl mb-3">Confirming your payment…</h1>
              <p className="text-zinc-400 text-sm">This usually takes a couple of seconds.</p>
            </>
          )}
          {isSuccess && state.status === "paid" && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-[#E50914]/15 border border-[#E50914]/40 flex items-center justify-center mb-6">
                <Check size={28} className="text-[#FF2A2D]" />
              </div>
              <h1 className="kd-display text-3xl mb-3">Payment received.</h1>
              <p className="text-zinc-400 text-sm mb-8">Taking you back to generate your deck…</p>
              <button data-testid="post-pay-continue" onClick={goToDeck} className="rounded-full px-6 py-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white transition-all duration-300 hover:shadow-[0_0_28px_rgba(229,9,20,0.5)] inline-flex items-center gap-2">
                Continue to my deck <ArrowRight size={16} />
              </button>
            </>
          )}
          {isSuccess && state.status === "unknown" && (
            <>
              <Loader2 size={32} className="mx-auto mb-6 animate-spin text-[#FF2A2D] opacity-50" />
              <h1 className="kd-display text-2xl mb-3">Almost there…</h1>
              <p className="text-zinc-400 text-sm mb-6">Stripe is finalising. Head to your deck — we&apos;ll pick it up from there.</p>
              <button data-testid="post-pay-continue" onClick={goToDeck} className="rounded-full px-6 py-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white transition-all duration-300 inline-flex items-center gap-2">
                Continue to my deck <ArrowRight size={16} />
              </button>
            </>
          )}
          {!isSuccess && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-white/5 border border-white/15 flex items-center justify-center mb-6">
                <X size={28} className="text-white/70" />
              </div>
              <h1 className="kd-display text-3xl mb-3">Checkout cancelled.</h1>
              <p className="text-zinc-400 text-sm mb-8">No charge was made. Head back whenever you&apos;re ready.</p>
              <button data-testid="post-cancel-back" onClick={() => nav("/dashboard", { replace: true })} className="rounded-full px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white">
                Back to my dashboard
              </button>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
